import { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScheduleUpload = ({ onUploadSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [schedulesToUpload, setSchedulesToUpload] = useState([]);
  const [uploadedSchedules, setUploadedSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({
    customer_id: '',
    item_id: '',
    start_date: '',
    end_date: '',
    qty: 0
  });
  const [loading, setLoading] = useState(false);
  const [showHolidayWarning, setShowHolidayWarning] = useState(false);
  const [holidayWarnings, setHolidayWarnings] = useState([]);

  useEffect(() => {
    fetchMasterData();
    fetchSchedules();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [custRes, itemRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/items`)
      ]);
      setCustomers(custRes.data);
      setItems(itemRes.data.filter(i => i.category === 'FG'));
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedule/monthly`);
      // Handle paginated response format
      const scheduleData = response.data.data || response.data;
      setUploadedSchedules(Array.isArray(scheduleData) ? scheduleData : []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setUploadedSchedules([]);
    }
  };

  const handleAddSchedule = () => {
    if (!newSchedule.customer_id || !newSchedule.item_id || !newSchedule.start_date || 
        !newSchedule.end_date || newSchedule.qty <= 0) {
      toast.error('Please fill all fields');
      return;
    }

    // Validate start date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(newSchedule.start_date);
    startDate.setHours(0, 0, 0, 0);
    
    if (startDate < today) {
      toast.error('Start date cannot be in the past. Please select today or a future date.');
      return;
    }

    // Validate date range
    if (new Date(newSchedule.end_date) < new Date(newSchedule.start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    const scheduleToAdd = {
      ...newSchedule,
      temp_id: Date.now()
    };

    setSchedulesToUpload([...schedulesToUpload, scheduleToAdd]);
    setNewSchedule({
      customer_id: '',
      item_id: '',
      start_date: '',
      end_date: '',
      qty: 0
    });
  };

  const handleRemoveSchedule = (tempId) => {
    setSchedulesToUpload(schedulesToUpload.filter(s => s.temp_id !== tempId));
  };

  const handleUpload = async () => {
    if (schedulesToUpload.length === 0) {
      toast.error('No schedules to upload');
      return;
    }

    setLoading(true);
    try {
      const payload = schedulesToUpload.map(s => ({
        customer_id: s.customer_id,
        item_id: s.item_id,
        start_date: s.start_date,
        end_date: s.end_date,
        qty: parseInt(s.qty)
      }));

      const response = await axios.post(`${API}/schedule/upload`, payload);
      
      // Check if response contains holiday warnings
      if (response.data.status === 'warning' && response.data.requires_confirmation) {
        // Show holiday warning dialog
        setHolidayWarnings(response.data.warnings);
        setShowHolidayWarning(true);
        setLoading(false);
        return;
      }
      
      // Normal success flow
      toast.success(`Uploaded ${response.data.uploaded} schedules, created ${response.data.daily_created} daily demands`);
      
      // Show MRP run reminder popup
      setTimeout(() => {
        toast.warning('⚠️ Demand has been changed. Please run MRP to update material requirements.', {
          duration: 6000,
          style: {
            background: '#FFA500',
            color: '#fff',
            fontWeight: 'bold'
          }
        });
      }, 1000);
      
      setSchedulesToUpload([]);
      fetchSchedules();
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmWithHolidays = async () => {
    setLoading(true);
    try {
      const payload = schedulesToUpload.map(s => ({
        customer_id: s.customer_id,
        item_id: s.item_id,
        start_date: s.start_date,
        end_date: s.end_date,
        qty: parseInt(s.qty)
      }));

      const response = await axios.post(`${API}/schedule/upload-confirmed`, payload);
      toast.success(`Uploaded ${response.data.uploaded} schedules, created ${response.data.daily_created} daily demands for working days only`);
      
      // Show MRP run reminder popup
      setTimeout(() => {
        toast.warning('⚠️ Demand has been changed. Please run MRP to update material requirements.', {
          duration: 6000,
          style: {
            background: '#FFA500',
            color: '#fff',
            fontWeight: 'bold'
          }
        });
      }, 1000);
      
      setSchedulesToUpload([]);
      setShowHolidayWarning(false);
      setHolidayWarnings([]);
      fetchSchedules();
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelHolidayWarning = () => {
    setShowHolidayWarning(false);
    setHolidayWarnings([]);
    setLoading(false);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this schedule and its related daily demands?')) return;
    
    try {
      await axios.delete(`${API}/schedule/${scheduleId}`);
      toast.success('Schedule deleted');
      
      // Show MRP run reminder popup
      setTimeout(() => {
        toast.warning('⚠️ Demand has been changed. Please run MRP to update material requirements.', {
          duration: 6000,
          style: {
            background: '#FFA500',
            color: '#fff',
            fontWeight: 'bold'
          }
        });
      }, 1000);
      
      fetchSchedules();
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear ALL schedules, daily demands, and MRP requirements? This cannot be undone!')) return;
    
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/schedule/all`);
      toast.success(`Cleared: ${response.data.schedules_deleted} schedules, ${response.data.daily_demands_deleted} demands, ${response.data.mrp_requirements_deleted} MRP records`);
      
      // Show MRP run reminder popup
      setTimeout(() => {
        toast.info('💡 Demands cleared. Run MRP after uploading new schedules.', {
          duration: 5000,
          style: {
            background: '#2196F3',
            color: '#fff'
          }
        });
      }, 1000);
      
      fetchSchedules();
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast.error('Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || id;
  const getItemDesc = (id) => items.find(i => i.id === id)?.description || id;
  const getItemPartNumber = (id) => items.find(i => i.id === id)?.part_number || '';

  return (
    <div className="space-y-6">
      <Card data-testid="schedule-entry-card">
        <CardHeader>
          <CardTitle>Add Schedule with Date Range</CardTitle>
          <CardDescription>Enter customer demand for a specific date range (time series)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Customer *</label>
              <Select
                data-testid="select-customer"
                value={newSchedule.customer_id}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, customer_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Item (FG) *</label>
              <SearchableItemSelect
                items={items}
                value={newSchedule.item_id}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, item_id: value === 'all' ? '' : value })}
                placeholder="Select FG item"
                showCategory={false}
                dataTestId="select-item"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Start Date *</label>
              <Input
                data-testid="input-start-date"
                type="date"
                value={newSchedule.start_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setNewSchedule({ ...newSchedule, start_date: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">Today or future date only</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">End Date *</label>
              <Input
                data-testid="input-end-date"
                type="date"
                value={newSchedule.end_date}
                min={newSchedule.start_date || new Date().toISOString().split('T')[0]}
                onChange={(e) => setNewSchedule({ ...newSchedule, end_date: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Total Quantity *</label>
              <Input
                data-testid="input-qty"
                type="number"
                min="0"
                value={newSchedule.qty}
                onChange={(e) => setNewSchedule({ ...newSchedule, qty: e.target.value })}
              />
            </div>
          </div>

          <Button data-testid="add-schedule-btn" onClick={handleAddSchedule} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Add to List
          </Button>
        </CardContent>
      </Card>

      {schedulesToUpload.length > 0 && (
        <Card data-testid="schedule-list-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Schedules to Upload</CardTitle>
                <CardDescription>{schedulesToUpload.length} schedule(s) ready</CardDescription>
              </div>
              <Button
                data-testid="upload-schedules-btn"
                onClick={handleUpload}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedulesToUpload.map((schedule) => (
                  <TableRow key={schedule.temp_id || schedule.id}>
                    <TableCell>{getCustomerName(schedule.customer_id)}</TableCell>
                    <TableCell className="font-mono text-sm">{getItemPartNumber(schedule.item_id)}</TableCell>
                    <TableCell>{getItemDesc(schedule.item_id)}</TableCell>
                    <TableCell>{schedule.start_date || schedule.month_year}</TableCell>
                    <TableCell>{schedule.end_date || '-'}</TableCell>
                    <TableCell>{schedule.qty}</TableCell>
                    <TableCell>
                      <Button
                        data-testid={`remove-schedule-${schedule.temp_id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSchedule(schedule.temp_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Uploaded Schedules */}
      {uploadedSchedules.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Uploaded Schedules</CardTitle>
                <CardDescription>{uploadedSchedules.length} schedule(s) in database</CardDescription>
              </div>
              <Button
                onClick={handleClearAll}
                disabled={loading}
                variant="destructive"
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploadedSchedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>{getCustomerName(schedule.customer_id)}</TableCell>
                    <TableCell className="font-mono text-sm">{getItemPartNumber(schedule.item_id)}</TableCell>
                    <TableCell>{getItemDesc(schedule.item_id)}</TableCell>
                    <TableCell>{schedule.start_date || schedule.month_year || '-'}</TableCell>
                    <TableCell>{schedule.end_date || '-'}</TableCell>
                    <TableCell>{schedule.qty}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSchedule(schedule.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Holiday Warning Dialog */}
      <Dialog open={showHolidayWarning} onOpenChange={setShowHolidayWarning}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-6 h-6" />
              Holiday Detected in Schedule
            </DialogTitle>
            <DialogDescription>
              Some dates in your schedule range include holidays. The system will exclude holiday dates and distribute quantity only across working days.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {holidayWarnings.map((warning, index) => (
              <div key={index} className="border border-orange-200 bg-orange-50 p-4 rounded-lg">
                <div className="font-semibold text-slate-800 mb-2">
                  {warning.customer} - {warning.item}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-600">Date Range:</span>
                    <span className="ml-2 font-medium">{warning.start_date} to {warning.end_date}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total Quantity:</span>
                    <span className="ml-2 font-medium">{warning.qty} units</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total Days:</span>
                    <span className="ml-2 font-medium">{warning.total_days} days</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Working Days:</span>
                    <span className="ml-2 font-medium text-green-600">{warning.working_days} days</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Holiday Days:</span>
                    <span className="ml-2 font-medium text-red-600">{warning.holiday_days} days</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Qty per Working Day:</span>
                    <span className="ml-2 font-medium text-blue-600">{warning.qty_per_working_day} units/day</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <span className="text-slate-600 text-sm">Holidays to be excluded:</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {warning.holidays.map((holiday, idx) => (
                      <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                        {holiday}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mt-4">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Note:</strong> The schedule will be created, but daily demands will only be generated for working days. Holiday dates will be automatically excluded.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelHolidayWarning} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmWithHolidays}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading ? 'Processing...' : 'Proceed with Working Days Only'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduleUpload;
